/**
 * Finance · Budget 360 Workspace (Wave 3).
 *
 * Questions this workspace answers:
 *   "Who approved this budget? Who spent it? Why is there variance?"
 *
 * Contract-driven per Objects 028 (Budget) · 023 (Expense) ·
 * 020 (Campaign) · 031 (Decision).
 *
 * Reuses existing primitives only.
 */
import { useMemo, useState } from "react";
import { Target } from "lucide-react";
import { Card, SectionHeader, DeltaChip } from "../../components/lib";
import { Drawer, Field, Segmented, ListRow } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { BUDGET, EXPENSES, BudgetLine } from "../../mocks/financeData";
import { CAMPAIGNS } from "../../mocks/marketingData";
import { DECISIONS } from "../../mocks/decisionsData";
import { COST_CENTERS } from "../../mocks/costCentersData";
import { CASH_FLOW, CASH_FORECAST_START_INDEX } from "../../mocks/financeData";

type BudTab =
  | "overview" | "owner" | "approver" | "revisions"
  | "transactions" | "variance" | "forecast" | "remaining"
  | "expenses" | "campaigns" | "decisions" | "audit";

/** Fuzzy match a budget line to an expense category label. */
function expenseCategoryMatchesBudget(bcat: string, ecat: string): boolean {
  const b = bcat.toLowerCase();
  if (ecat === "cogs" && (b.includes("cogs") || b.includes("materials") || b.includes("packaging"))) return true;
  if (ecat === "marketing" && b.includes("marketing")) return true;
  if (ecat === "salaries" && (b.includes("salaries") || b.includes("payroll") || b.includes("hr"))) return true;
  if (ecat === "logistics" && (b.includes("logistics") || b.includes("shipping"))) return true;
  if (ecat === "rent" && b.includes("rent")) return true;
  if (ecat === "software" && b.includes("software")) return true;
  return false;
}

export default function Budget() {
  const [selected, setSelected] = useState<BudgetLine | null>(null);
  const [wsTab, setWsTab] = useState<BudTab>("overview");

  const totalPlanned = BUDGET.reduce((n, b) => n + b.plannedEGP, 0);
  const totalActual  = BUDGET.reduce((n, b) => n + b.actualEGP, 0);
  const variance     = totalActual - totalPlanned;
  const overLines    = BUDGET.filter((b) => b.actualEGP > b.plannedEGP).length;

  const lineData = useMemo(() => {
    if (!selected) return null;
    // Expenses tagged to this budget line (by category fuzzy-match or cost-centre owner)
    const linkedExpenses = EXPENSES.filter((e) => {
      if (expenseCategoryMatchesBudget(selected.category, e.category)) return true;
      const cc = (e as unknown as { costCenter?: string }).costCenter;
      if (cc && (cc.toLowerCase() === selected.ownerDepartment.toLowerCase())) return true;
      return false;
    });
    // Marketing campaigns for marketing budget line
    const linkedCampaigns = selected.category.toLowerCase().includes("marketing")
      ? CAMPAIGNS
      : CAMPAIGNS.filter((c) => c.owner && c.owner.toLowerCase().includes(selected.ownerDepartment.toLowerCase()));
    // Decisions linked to the department / category
    const linkedDecisions = DECISIONS.filter((d) =>
      d.module === "Finance" ||
      (d.rationale && (
        d.rationale.toLowerCase().includes(selected.category.toLowerCase()) ||
        d.rationale.toLowerCase().includes(selected.ownerDepartment.toLowerCase())
      ))
    );
    const cc = COST_CENTERS.find((c) => c.name.toLowerCase() === selected.ownerDepartment.toLowerCase());
    // Forecast (extrapolate quarter-end)
    const monthsElapsed = CASH_FORECAST_START_INDEX; // months in "actual" bucket
    const monthsInPeriod = CASH_FLOW.length;         // quarter total
    const runRate = monthsElapsed > 0 ? selected.actualEGP / monthsElapsed : 0;
    const projected = runRate * monthsInPeriod;
    const remaining = Math.max(0, selected.plannedEGP - selected.actualEGP);
    return { linkedExpenses, linkedCampaigns, linkedDecisions, cc, projected, remaining, runRate };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="fn-budget">
      <Card testId="fn-bud-summary">
        <SectionHeader eyebrow="Q3 2026 · Who approved, who spent, why the variance?" title="Budget summary" action={<Target size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Planned</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {(totalPlanned / 1000000).toFixed(2)}M</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Actual</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {(totalActual / 1000000).toFixed(2)}M</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Variance</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: variance > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>
              {variance >= 0 ? "+" : ""}EGP {(variance / 1000).toFixed(0)}K
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Utilisation</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-brand-primary)" }}>{Math.round((totalActual / Math.max(1, totalPlanned)) * 100)}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Lines over</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: overLines > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>{overLines}</div>
          </div>
        </div>
      </Card>

      <Card padded={false} testId="fn-bud-lines">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Lines · click to drill" title="Planned vs actual by category" />
        </div>
        <div className="p-5 space-y-2">
          {BUDGET.map((b) => {
            const overPct   = Math.min(100, (b.actualEGP / Math.max(b.plannedEGP, 1)) * 100);
            const over      = b.actualEGP > b.plannedEGP;
            const barColor  = over ? "var(--ph-danger)" : "var(--ph-success)";
            const lineVar   = b.actualEGP - b.plannedEGP;
            return (
              <button
                key={b.key}
                data-testid={`fn-bud-${b.key}`}
                onClick={() => { setSelected(b); setWsTab("overview"); }}
                className="grid items-center py-2 w-full text-left transition-colors"
                style={{ gridTemplateColumns: "1.4fr 3fr 1fr 1fr 1fr", borderTop: "1px solid var(--ph-border-subtle)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ph-hover-bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{b.category}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>{b.ownerDepartment}</div>
                </div>
                <div>
                  <div className="h-2 rounded relative" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                    <div style={{ width: `${overPct}%`, height: "100%", background: barColor, transition: "width 0.3s" }} />
                  </div>
                  <div className="mt-1 text-[10px] flex justify-between" style={{ color: "var(--ph-ink-muted)" }}>
                    <span>0</span>
                    <span>plan · EGP {(b.plannedEGP / 1000).toFixed(0)}K</span>
                  </div>
                </div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-default)" }}>EGP {(b.plannedEGP / 1000).toFixed(0)}K</div>
                <div className="text-sm tabular-nums text-right font-semibold" style={{ color: "var(--ph-ink-strong)" }}>EGP {(b.actualEGP / 1000).toFixed(0)}K</div>
                <div className="text-sm tabular-nums text-right font-semibold" style={{ color: over ? "var(--ph-danger)" : "var(--ph-success)" }}>
                  {lineVar >= 0 ? "+" : ""}EGP {(lineVar / 1000).toFixed(0)}K
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Budget 360" testId="fn-bud-drawer" width={720}>
        {selected && lineData && (() => {
          const util = selected.plannedEGP ? (selected.actualEGP / selected.plannedEGP) * 100 : 0;
          const lineVar = selected.actualEGP - selected.plannedEGP;
          const over = lineVar > 0;
          return (
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-brand-primary)" }}>{selected.ownerDepartment}</div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.category}</div>

              <div className="grid grid-cols-4 gap-3 mb-4">
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Planned</div><div className="text-sm font-semibold tabular-nums">EGP {(selected.plannedEGP / 1000).toFixed(0)}K</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Actual</div><div className="text-sm font-semibold tabular-nums">EGP {(selected.actualEGP / 1000).toFixed(0)}K</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Variance</div><div className="text-sm font-semibold tabular-nums" style={{ color: over ? "var(--ph-danger)" : "var(--ph-success)" }}>{lineVar >= 0 ? "+" : ""}EGP {(lineVar / 1000).toFixed(0)}K</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Utilisation</div><div className="text-sm font-semibold tabular-nums" style={{ color: util >= 100 ? "var(--ph-danger)" : "var(--ph-success)" }}>{util.toFixed(0)}%</div></div>
              </div>

              <div className="mb-4 overflow-x-auto">
                <Segmented<BudTab>
                  testId="fn-bud-ws-tabs"
                  value={wsTab}
                  onChange={setWsTab}
                  options={[
                    { value: "overview",     label: "Overview"     },
                    { value: "owner",        label: "Owner"        },
                    { value: "approver",     label: "Approver"     },
                    { value: "revisions",    label: "Revisions"    },
                    { value: "transactions", label: "Transactions" },
                    { value: "variance",     label: "Variance"     },
                    { value: "forecast",     label: "Forecast"     },
                    { value: "remaining",    label: "Remaining"    },
                    { value: "expenses",     label: "Expenses"     },
                    { value: "campaigns",    label: "Campaigns"    },
                    { value: "decisions",    label: "Decisions"    },
                    { value: "audit",        label: "Audit"        },
                  ]}
                />
              </div>

              {wsTab === "overview" && (
                <div data-testid="fn-bud-ws-overview">
                  <Field label="Category"    inline><span className="text-sm">{selected.category}</span></Field>
                  <Field label="Department"  inline><span className="text-sm">{selected.ownerDepartment}</span></Field>
                  <Field label="Planned"     inline><span className="text-sm tabular-nums">EGP {selected.plannedEGP.toLocaleString()}</span></Field>
                  <Field label="Actual"      inline><span className="text-sm tabular-nums">EGP {selected.actualEGP.toLocaleString()}</span></Field>
                  <Field label="Remaining"   inline><span className="text-sm tabular-nums" style={{ color: lineData.remaining > 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>EGP {lineData.remaining.toLocaleString()}</span></Field>
                  <Field label="Utilisation" inline><span className="text-sm tabular-nums" style={{ color: util >= 100 ? "var(--ph-danger)" : "var(--ph-success)" }}>{util.toFixed(0)}%</span></Field>
                  <Field label="Trend"       inline><DeltaChip delta={((selected.actualEGP - selected.plannedEGP) / Math.max(1, selected.plannedEGP)) * 100} trend={over ? "up" : "down"} /></Field>
                </div>
              )}

              {wsTab === "owner" && (
                <div data-testid="fn-bud-ws-owner">
                  <Field label="Owner department" inline><span className="text-sm font-semibold">{selected.ownerDepartment}</span></Field>
                  {lineData.cc ? (
                    <>
                      <Field label="Cost centre owner" inline><span className="text-sm">{lineData.cc.owner}</span></Field>
                      <Field label="Cost centre code"  inline><span className="text-sm font-mono">{lineData.cc.code}</span></Field>
                      <Field label="CC budget"         inline><span className="text-sm tabular-nums">EGP {lineData.cc.budgetEGP.toLocaleString()}</span></Field>
                      <Field label="CC spent"          inline><span className="text-sm tabular-nums">EGP {lineData.cc.spentEGP.toLocaleString()}</span></Field>
                    </>
                  ) : (
                    <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No cost-centre master record found for {selected.ownerDepartment}.</div>
                  )}
                </div>
              )}

              {wsTab === "approver" && (
                <div data-testid="fn-bud-ws-approver">
                  <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                    Budget approved by department head + CFO. See Approvals queue for individual line-item approvals and delegations.
                  </div>
                  <Field label="Department head" inline><span className="text-sm">{lineData.cc?.owner || `${selected.ownerDepartment} lead`}</span></Field>
                  <Field label="Approver"        inline><span className="text-sm">CFO (Hassan)</span></Field>
                  <Field label="Approval status" inline><span className="text-sm" style={{ color: "var(--ph-success)" }}>Approved · Q3 2026</span></Field>
                </div>
              )}

              {wsTab === "revisions" && (
                <div data-testid="fn-bud-ws-revisions">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Revision history</div>
                  <ListRow testId="fn-bud-rev-current"
                    label={<>Q3 2026 · Current</>}
                    sublabel={<>Approved · Planned EGP {(selected.plannedEGP / 1000).toFixed(0)}K</>}
                    trailing={<span className="text-[10px]" style={{ color: "var(--ph-success)" }}>ACTIVE</span>}
                  />
                  <ListRow testId="fn-bud-rev-prev"
                    label={<>Q3 2026 · Draft v0</>}
                    sublabel={<>Planned EGP {((selected.plannedEGP * 0.9) / 1000).toFixed(0)}K · superseded</>}
                    trailing={<span className="text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>ARCHIVED</span>}
                  />
                </div>
              )}

              {wsTab === "transactions" && (
                <div data-testid="fn-bud-ws-tx">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Booked transactions · {lineData.linkedExpenses.length}</div>
                  {lineData.linkedExpenses.slice(0, 20).map((e) => (
                    <ListRow key={e.id} testId={`fn-bud-tx-${e.id}`}
                      label={e.vendor}
                      sublabel={<>{e.description} · {e.status}</>}
                      trailing={<span className="text-sm tabular-nums">EGP {e.amountEGP.toLocaleString()}</span>}
                    />
                  ))}
                </div>
              )}

              {wsTab === "variance" && (
                <div data-testid="fn-bud-ws-variance">
                  <Field label="Absolute variance" inline><span className="text-sm tabular-nums font-semibold" style={{ color: over ? "var(--ph-danger)" : "var(--ph-success)" }}>{lineVar >= 0 ? "+" : ""}EGP {lineVar.toLocaleString()}</span></Field>
                  <Field label="Relative variance" inline><span className="text-sm tabular-nums" style={{ color: over ? "var(--ph-danger)" : "var(--ph-success)" }}>{selected.plannedEGP ? ((lineVar / selected.plannedEGP) * 100).toFixed(1) : "0"}%</span></Field>
                  <div className="mt-3 text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                    {over
                      ? `${selected.category} is trending over-plan. Top drivers: ${lineData.linkedExpenses.slice(0, 3).map((e) => e.vendor).join(", ") || "n/a"}.`
                      : `${selected.category} is under-plan. Room remaining: EGP ${lineData.remaining.toLocaleString()}.`}
                  </div>
                </div>
              )}

              {wsTab === "forecast" && (
                <div data-testid="fn-bud-ws-forecast">
                  <Field label="Run rate / month"     inline><span className="text-sm tabular-nums">EGP {lineData.runRate.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                  <Field label="Quarter-end forecast" inline><span className="text-sm tabular-nums font-semibold" style={{ color: lineData.projected > selected.plannedEGP ? "var(--ph-danger)" : "var(--ph-success)" }}>EGP {lineData.projected.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                  <Field label="Projected variance"   inline><span className="text-sm tabular-nums" style={{ color: lineData.projected > selected.plannedEGP ? "var(--ph-danger)" : "var(--ph-success)" }}>{lineData.projected - selected.plannedEGP >= 0 ? "+" : ""}EGP {(lineData.projected - selected.plannedEGP).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                </div>
              )}

              {wsTab === "remaining" && (
                <div data-testid="fn-bud-ws-remaining">
                  <Field label="Remaining budget" inline><span className="text-sm tabular-nums font-semibold" style={{ color: lineData.remaining > 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>EGP {lineData.remaining.toLocaleString()}</span></Field>
                  <Field label="% of plan remaining" inline><span className="text-sm tabular-nums">{selected.plannedEGP ? ((lineData.remaining / selected.plannedEGP) * 100).toFixed(0) : "0"}%</span></Field>
                  <Field label="Weeks of runway at run rate" inline><span className="text-sm tabular-nums">{lineData.runRate > 0 ? (lineData.remaining / (lineData.runRate / 4.33)).toFixed(1) : "—"}</span></Field>
                </div>
              )}

              {wsTab === "expenses" && (
                <div data-testid="fn-bud-ws-expenses">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Linked expenses · {lineData.linkedExpenses.length}</div>
                  {lineData.linkedExpenses.map((e) => (
                    <ListRow key={e.id} testId={`fn-bud-exp-${e.id}`}
                      label={e.vendor}
                      sublabel={<>{e.description} · {e.category} · {e.status}</>}
                      trailing={<span className="text-sm tabular-nums">EGP {e.amountEGP.toLocaleString()}</span>}
                    />
                  ))}
                </div>
              )}

              {wsTab === "campaigns" && (
                <div data-testid="fn-bud-ws-campaigns">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Linked campaigns · {lineData.linkedCampaigns.length}</div>
                  {lineData.linkedCampaigns.slice(0, 10).map((c) => (
                    <ListRow key={c.id} testId={`fn-bud-cmp-${c.id}`}
                      label={c.name}
                      sublabel={<>{c.status} · ROAS {c.roas.toFixed(2)}× · {c.owner}</>}
                      trailing={<span className="text-sm tabular-nums">EGP {(c.spendEGP || 0).toLocaleString()}</span>}
                    />
                  ))}
                </div>
              )}

              {wsTab === "decisions" && (
                <div data-testid="fn-bud-ws-decisions">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Linked decisions · {lineData.linkedDecisions.length}</div>
                  {lineData.linkedDecisions.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No decisions linked to this line yet.</div>}
                  {lineData.linkedDecisions.slice(0, 10).map((d) => (
                    <ListRow key={d.id} testId={`fn-bud-dec-${d.id}`}
                      label={d.title}
                      sublabel={<>{d.outcome} · {d.decidedBy} · {d.dateRelative}</>}
                      trailing={d.budgetImpactEGP ? <span className="text-sm tabular-nums">EGP {d.budgetImpactEGP.toLocaleString()}</span> : null}
                    />
                  ))}
                </div>
              )}

              {wsTab === "audit" && (
                <div data-testid="fn-bud-ws-audit">
                  <AuditTrail collection="budget" recordId={selected.key} testId="fn-bud-audit" />
                </div>
              )}
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}
